import { useState, useEffect } from "react";
import { clsx } from "clsx";
import { LOAN_PURPOSE_MORTGAGE_REFINANCE } from "@/lib/constants";
import { useFormDataStore } from "@/components/context/useFormContext";
import { useFormStateStore } from "@/components/context/useFormContext";
import { ToggleSwitch } from "@/components/ui/toggleSwitch";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import { secureInput } from "@/lib/security";

export const PropertyDetails = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { setFormError, enabledFields, formContainer, selectedIndex, setValidateFormDetails } =
		useFormStateStore();

	const {
		loanType,
		loanPurpose,
		assetType,
		purchaseStage,
		isFirstHomeBuyer,
		propertyValue,
		propertyDeposit,
		existingLender,
		refinancePurpose,
		existingLoanBalance,
		isInFixedRate,
		fixedRateEndDate,
		interestRate,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		assetType: false,
		purchaseStage: false,
		isFirstHomeBuyer: false,
		propertyValue: false,
		propertyDeposit: false,
		existingLender: false,
		refinancePurpose: false,
		existingLoanBalance: false,
		isInFixedRate: false,
		fixedRateEndDate: false,
		interestRate: false,
	});

	// Toggle Switch for Fixed Rate
	const [isFixedRate, setIsFixedRate] = useState(isInFixedRate === "Yes" ? true : false);
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	useEffect(() => {
		setFormValue("isInFixedRate", isFixedRate ? "Yes" : "No");
	}, [isFixedRate]);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			assetType:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? false
					: enabledFields.assetType?.length == 0
					? false
					: !assetType,
			purchaseStage:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? false
					: enabledFields.purchaseStage?.length == 0
					? false
					: !purchaseStage,
			isFirstHomeBuyer:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? false
					: enabledFields.isFirstHomeBuyer?.length == 0
					? false
					: !isFirstHomeBuyer,
			propertyValue: !propertyValue || parseInt((propertyValue || "0").replace(/,/g, "")) < 1,
			propertyDeposit:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE ? false : !propertyDeposit,
			existingLender:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE ? !existingLender : false,
			refinancePurpose:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? enabledFields.refinancePurpose?.length == 0
						? false
						: !refinancePurpose
					: false,
			existingLoanBalance:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? !existingLoanBalance ||
					  parseInt((existingLoanBalance || "0").replace(/,/g, "")) < 1
					: false,
			isInFixedRate:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? enabledFields.isInFixedRate?.length == 0
						? false
						: !isInFixedRate
					: false,
			fixedRateEndDate:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE && isFixedRate
					? !fixedRateEndDate
					: false,
			interestRate:
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
					? !secureInput.rate(interestRate).isValid || parseFloat(interestRate) <= 0
					: false,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [
		loanPurpose,
		assetType,
		purchaseStage,
		isFirstHomeBuyer,
		propertyValue,
		propertyDeposit,
		existingLender,
		refinancePurpose,
		existingLoanBalance,
		isInFixedRate,
		isFixedRate,
		fixedRateEndDate,
		interestRate,
	]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = [
			"loanPurpose",
			"assetType",
			"purchaseStage",
			"isFirstHomeBuyer",
			"propertyValue",
			"propertyDeposit",
			"existingLender",
			"refinancePurpose",
			"existingLoanBalance",
			"isInFixedRate",
			"fixedRateEndDate",
			"interestRate",
		];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	const validateFixedRateEndDate = (date: string) => {
		const today = new Date();
		const selectedDate = new Date(date);
		return selectedDate > today;
	};

	const handleInterestRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.rate(value);
		setFormValue("interestRate", sanitizedValue.sanitized);
		setErrors((prevErrors) => ({
			...prevErrors,
			interestRate: !sanitizedValue.isValid,
		}));
	};

	return (
		<div className="jF-space-y-4">
			{/* Property Type is for Mortgage. Asset Type is for Boats and Equipments. */}
			{enabledFields.assetType?.length != 0 &&
				loanPurpose !== LOAN_PURPOSE_MORTGAGE_REFINANCE && (
					<RadioGroupField
						id="assetType"
						label="Asset Type"
						value={assetType || ""}
						options={enabledFields.assetType.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("assetType", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								assetType: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.assetType ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

			{/* Purchase Stage */}
			{enabledFields.purchaseStage?.length != 0 &&
				loanPurpose !== LOAN_PURPOSE_MORTGAGE_REFINANCE && (
					<RadioGroupField
						id="purchaseStage"
						label="Do you already have one in mind?"
						value={purchaseStage || ""}
						options={enabledFields.purchaseStage.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("purchaseStage", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								purchaseStage: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.purchaseStage ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={3}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

			{/* First Time Home Buyer */}
			{enabledFields.isFirstHomeBuyer?.length != 0 &&
				loanPurpose !== LOAN_PURPOSE_MORTGAGE_REFINANCE && (
					<RadioGroupField
						id="isFirstHomeBuyer"
						label="Are you a first home buyer?"
						value={isFirstHomeBuyer || ""}
						options={enabledFields.isFirstHomeBuyer.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("isFirstHomeBuyer", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								isFirstHomeBuyer: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.isFirstHomeBuyer ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

			{/* Refinancing Purpose */}
			{enabledFields.refinancePurpose?.length != 0 &&
				loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE && (
					<RadioGroupField
						id="refinancePurpose"
						label="What is the purpose of your refinance?"
						value={refinancePurpose || ""}
						options={enabledFields.refinancePurpose?.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("refinancePurpose", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								isFirstHomeBuyer: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.refinancePurpose ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

			{/* Lender Details */}
			{loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						What is the name of your existing lender?
					</label>
					<input
						value={existingLender || ""}
						onChange={(e) => {
							setFormValue("existingLender", e.target.value);
							setErrors((prevErrors) => ({
								...prevErrors,
								existingLender: !e.target.value,
							}));
						}}
						aria-label="Existing Lender"
						type="text"
						id="existingLender"
						placeholder="Existing Lender"
						maxLength={80}
						className={clsx(
							errors.existingLender
								? "jF-ring-red-500 focus:jF-ring-red-500"
								: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
							"jF-block jF-mt-2 jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6"
						)}
					/>
					{errors.existingLender && (
						<span className="jF-text-sm jF-text-red-500">Existing Lender Required</span>
					)}
				</div>
			)}

			{/* Property Value */}
			<div>
				<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
					{loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE
						? "What is your property's current market value?"
						: "What is the purchase price of the property?"}
				</label>
				<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
					<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
						<span className="jF-text-gray-500 sm:jF-text-md">$</span>
					</div>
					<input
						value={propertyValue || ""}
						type="text"
						id="propertyValue"
						aria-describedby="propertyValue"
						inputMode="numeric"
						aria-label="Property Value"
						maxLength={11}
						onChange={(e) => {
							const value = +e.target.value.replace(/[^0-9]/g, "");
							setFormValue("propertyValue", value.toLocaleString());
							setErrors((prevErrors) => ({
								...prevErrors,
								propertyValue: !value,
							}));
						}}
						className={clsx(
							errors.propertyValue
								? "jF-ring-red-500 focus:jF-ring-red-500"
								: "jF-ring-gray-300 focus:jF-ring-gray-900",
							" jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset  placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
						)}
					/>
					<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
						<span className="jF-text-gray-500 sm:jF-text-sm">AUD</span>
					</div>
				</div>
				{errors.propertyValue && (
					<span className="jF-text-sm jF-text-red-500">Property Value is required</span>
				)}
			</div>

			{/* Deposit Value */}
			{loanPurpose !== LOAN_PURPOSE_MORTGAGE_REFINANCE && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						How much deposit do you have?
					</label>
					<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={propertyDeposit || ""}
							type="text"
							id="propertyDeposit"
							aria-describedby="propertyDeposit"
							inputMode="numeric"
							aria-label="Property Deposit"
							maxLength={11}
							onChange={(e) => {
								const value = +e.target.value.replace(/[^0-9]/g, "");
								setFormValue("propertyDeposit", value.toLocaleString());
								setErrors((prevErrors) => ({
									...prevErrors,
									propertyDeposit: !value,
								}));
							}}
							className={clsx(
								errors.propertyDeposit
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-gray-900",
								" jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset  placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
							)}
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-sm">AUD</span>
						</div>
					</div>
					{errors.propertyDeposit && (
						<span className="jF-text-sm jF-text-red-500">
							Property Deposit is required
						</span>
					)}
				</div>
			)}

			{/* Existing Loan Amount / Balance*/}
			{loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						What is the estimated balance on your current home loan?
					</label>
					<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={existingLoanBalance || ""}
							type="text"
							id="existingLoanBalance"
							aria-describedby="existingLoanBalance"
							inputMode="numeric"
							aria-label="Existing Loan Amount"
							maxLength={11}
							onChange={(e) => {
								const value = +e.target.value.replace(/[^0-9]/g, "");
								setFormValue("existingLoanBalance", value.toLocaleString());
								setErrors((prevErrors) => ({
									...prevErrors,
									existingLoanBalance: !value,
								}));
							}}
							className={clsx(
								errors.existingLoanBalance
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-gray-900",
								" jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset  placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
							)}
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-sm">AUD</span>
						</div>
					</div>
					{errors.existingLoanBalance && (
						<span className="jF-text-sm jF-text-red-500">
							Existing Loan Amount is required
						</span>
					)}
				</div>
			)}

			{loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE && (
				<div>
					<div className="jF-flex-1 jF-col-span-1 jF-block jF-mb-4">
						<div className="jF-w-full">
							<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
								What is your current interest rate?
							</label>
							<div className="jF-relative jF-mt-2">
								<input
									type="number"
									inputMode="decimal"
									id="interestRate"
									step="0.01"
									value={interestRate || ""}
									placeholder="Enter interest rate"
									onChange={handleInterestRateChange}
									onWheel={(e) => (e.target as HTMLInputElement).blur()}
									className={clsx(
										errors.interestRate
											? "jF-ring-red-500 focus:jF-ring-red-500"
											: "jF-ring-gray-300 focus:jF-ring-gray-900",
										" jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset  placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
									)}
								/>
								<span className="jF-absolute jF-right-3 jF-top-1/2 jF-transform jF--translate-y-1/2 jF-text-gray-500">
									% p.a.
								</span>
							</div>
							{errors.interestRate && (
								<p className="jF-mt-1 jF-text-sm jF-text-red-600">
									Interest Rate is required
								</p>
							)}
						</div>
					</div>

					<ToggleSwitch
						label="Is your current home loan on a fixed rate?"
						enabled={isFixedRate}
						setEnabled={setIsFixedRate}
					/>

					{isFixedRate && (
						<div className="jF-mt-4">
							<label
								htmlFor="fixedRateEndDate"
								className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900"
							>
								When does your current fixed rate period end?
							</label>
							<input
								type="date"
								id="fixedRateEndDate"
								value={fixedRateEndDate || new Date().toISOString().split("T")[0]}
								onChange={(e) => {
									if (validateFixedRateEndDate(e.target.value)) {
										setFormValue("fixedRateEndDate", e.target.value);
									}
									setErrors((prevErrors) => ({
										...prevErrors,
										fixedRateEndDate: !e.target.value,
									}));
								}}
								className={clsx(
									errors.fixedRateEndDate
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-gray-900",
									"jF-block jF-w-full jF-mt-2 jF-rounded-md jF-border-0 jF-py-2.5 jF-px-3 jF-text-gray-900 jF-ring-1 jF-ring-inset placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
								)}
							/>
							{errors.fixedRateEndDate && (
								<span className="jF-text-sm jF-text-red-500">
									Fixed Rate End Date is required and must be in the future
								</span>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
